import { addBreadcrumb } from 'shared/sentry';

/**
 * ICE / TURN helpers aligned with matrix-js-sdk MatrixCall (createPeerConnection,
 * placeCallWithCallFeeds → checkTurnServers). The SDK snapshots TURN at PC creation;
 * after credential TTL expiry or TURN outage, callers must fetch fresh creds and
 * apply them via setConfiguration before restartIce().
 */

const TURN_FETCH_TIMEOUT_MS = 8000;
/** Homeserver load: avoid calling checkTurnServers on every ICE restart during long calls */
const TURN_CHECK_MIN_INTERVAL_MS = 60_000;
/** Per-call: avoid restart storms from overlapping triggers (bypassed when ICE is `failed`) */
const RESTART_MIN_INTERVAL_MS = 3000;
/** When signaling is not stable, retry ICE recovery shortly after */
const SIGNALING_RETRY_MS = 1000;
/** Post-restart diagnostics delay */
const POST_RESTART_LOG_MS = 2000;

/** @type {WeakMap<object, number>} */
const lastTurnCheckByClient = new WeakMap();
const TURN_CHECK_FALLBACK_KEY = {};

/** @type {WeakSet<object>} */
const restartInProgress = new WeakSet();
/** @type {WeakMap<object, number>} */
const lastRestartAtByCall = new WeakMap();
/** @type {WeakMap<object, ReturnType<typeof setTimeout>>} */
const postRestartLogTimersByCall = new WeakMap();
/** @type {WeakMap<object, ReturnType<typeof setTimeout>>} */
const signalingRetryTimersByCall = new WeakMap();

function turnCheckKey(client) {
  return client && typeof client === 'object' ? client : TURN_CHECK_FALLBACK_KEY;
}

/**
 * @param {import('matrix-js-sdk/lib/webrtc/call').MatrixCall | null | undefined} call
 * @param {RTCPeerConnection | null | undefined} pc
 */
function isStaleCall(call, pc) {
  if (!call || call.callHasEnded?.()) return true;
  if (pc && call.peerConn !== pc) return true;
  return false;
}

/**
 * Clear timers scheduled by this module for a call (invoke from session cleanup / hangup).
 * @param {import('matrix-js-sdk/lib/webrtc/call').MatrixCall | null | undefined} call
 */
export function clearIceRecoveryTimers(call) {
  if (!call) return;
  const post = postRestartLogTimersByCall.get(call);
  if (post) {
    clearTimeout(post);
    postRestartLogTimersByCall.delete(call);
  }
  const sig = signalingRetryTimersByCall.get(call);
  if (sig) {
    clearTimeout(sig);
    signalingRetryTimersByCall.delete(call);
  }
}

/** @param {Promise<T>} promise @param {number} ms @returns {Promise<T>} */
async function withTimeout(promise, ms) {
  let timeoutId;
  let settled = false;

  const wrapped = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error('timeout'));
      }
    }, ms);

    promise.then(
      (val) => {
        if (!settled) {
          settled = true;
          resolve(val);
        }
      },
      (err) => {
        if (!settled) {
          settled = true;
          reject(err);
        }
      }
    );
  });

  try {
    return await wrapped;
  } finally {
    clearTimeout(timeoutId);
  }
}

const log = (...args) => {
  try {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') {
      addBreadcrumb({
        category: 'matrix.call.ice',
        message: args.map((a) => (typeof a === 'string' ? a : JSON.stringify(a))).join(' '),
        level: 'info',
      });
    }
  } catch (_) {
    /* ignore */
  }
};

/** @param {import('matrix-js-sdk').MatrixClient | null | undefined} client */
function buildRtcIceServersFromMatrixClient(client) {
  if (!client) return [];
  const raw = typeof client.getTurnServers === 'function' ? client.getTurnServers() : [];
  const servers = Array.isArray(raw) ? raw : [];
  const out = servers.map((s) => {
    if (!s || !s.urls) return null;
    const entry = {
      urls: s.urls,
    };
    if (s.username != null && s.username !== '') entry.username = s.username;
    if (s.credential != null && s.credential !== '') entry.credential = s.credential;
    return entry;
  }).filter(Boolean);

  if (typeof client.isFallbackICEServerAllowed === 'function' && client.isFallbackICEServerAllowed()) {
    out.push({
      urls: ['stun:stun.l.google.com:19302', 'stun:global.stun.twilio.com:3478'],
    });
  }
  return out;
}

/**
 * Refresh TURN credentials from the homeserver (same as MatrixCall before createPeerConnection),
 * push them into the peer connection, then ICE restart.
 *
 * @param {import('matrix-js-sdk/lib/webrtc/call').MatrixCall | null | undefined} call
 * @param {import('matrix-js-sdk').MatrixClient | null | undefined} client
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
const WAIT_PC_INTERVAL_MS = 250;
const WAIT_PC_MAX_ATTEMPTS = 16;

export async function refreshTurnServersAndRestartIce(call, client) {
  if (!call || call.callHasEnded?.()) {
    return { ok: false, reason: 'no_call' };
  }

  let pc = call.peerConn;
  if (!pc) {
    for (let i = 0; i < WAIT_PC_MAX_ATTEMPTS; i++) {
      if (call.callHasEnded?.()) {
        return { ok: false, reason: 'call_ended_during_wait' };
      }
      await new Promise((r) => setTimeout(r, WAIT_PC_INTERVAL_MS));
      pc = call.peerConn;
      if (pc) break;
    }
  }
  if (!pc || call.callHasEnded?.()) {
    return { ok: false, reason: 'no_peer_connection' };
  }

  const iceState = pc.iceConnectionState;
  if (iceState === 'connected' || iceState === 'completed') {
    log('skip ICE restart', { iceState, reason: 'already_connected' });
    return { ok: false, reason: 'already_connected' };
  }

  if (restartInProgress.has(call)) {
    log('skip ICE restart', { reason: 'restart_in_progress' });
    return { ok: false, reason: 'restart_in_progress' };
  }

  const iceFailedForPolicy = pc.iceConnectionState === 'failed';
  const lastRestart = lastRestartAtByCall.get(call) || 0;
  if (!iceFailedForPolicy && Date.now() - lastRestart < RESTART_MIN_INTERVAL_MS) {
    log('skip ICE restart', { reason: 'restart_rate_limited' });
    return { ok: false, reason: 'restart_rate_limited' };
  }

  restartInProgress.add(call);

  try {
    try {
      const ck = turnCheckKey(client);
      const lastCheck = lastTurnCheckByClient.get(ck) || 0;
      /** When ICE has already failed, always refresh TURN from the homeserver — cached creds are a common cause of repeat failures. */
      const mustRefreshTurn =
        iceFailedForPolicy || Date.now() - lastCheck > TURN_CHECK_MIN_INTERVAL_MS;
      if (client && typeof client.checkTurnServers === 'function' && mustRefreshTurn) {
        await withTimeout(client.checkTurnServers(), TURN_FETCH_TIMEOUT_MS);
        lastTurnCheckByClient.set(ck, Date.now());
      }
    } catch (err) {
      log('checkTurnServers failed or timed out, continuing with getTurnServers()', err);
    }

    if (isStaleCall(call, pc)) {
      return { ok: false, reason: 'call_stale_or_ended' };
    }

    const iceServers = buildRtcIceServersFromMatrixClient(client);
    if (!iceServers.length) {
      log('no ICE servers after build');
      return { ok: false, reason: 'no_ice_servers' };
    }

    pc = call.peerConn;
    if (!pc || isStaleCall(call, pc)) {
      return { ok: false, reason: 'peer_connection_replaced' };
    }

    const existingConfig = (typeof pc.getConfiguration === 'function' && pc.getConfiguration()) || {};

    try {
      pc.setConfiguration({
        ...existingConfig,
        iceServers,
        iceTransportPolicy: client?.forceTURN ? 'relay' : existingConfig.iceTransportPolicy,
      });
      log('setConfiguration applied', { iceServersCount: iceServers.length, forceTURN: !!client?.forceTURN });
    } catch (err) {
      log('setConfiguration failed', err);
      return { ok: false, reason: 'set_configuration_failed' };
    }

    if (pc.signalingState !== 'stable') {
      log('defer ICE restart: signaling not stable', {
        signalingState: pc.signalingState,
        iceState: pc.iceConnectionState,
        iceGatheringState: pc.iceGatheringState,
      });
      const prev = signalingRetryTimersByCall.get(call);
      if (prev) clearTimeout(prev);
      const t = setTimeout(() => {
        signalingRetryTimersByCall.delete(call);
        try {
          if (!call.callHasEnded?.()) {
            void refreshTurnServersAndRestartIce(call, client);
          }
        } catch (_) {
          /* ignore */
        }
      }, SIGNALING_RETRY_MS);
      signalingRetryTimersByCall.set(call, t);
      return { ok: false, reason: 'signaling_not_stable' };
    }

    try {
      if (typeof pc.restartIce === 'function') {
        if (isStaleCall(call, pc)) {
          return { ok: false, reason: 'call_stale_before_restart' };
        }

        log('ICE debug (pre-restart)', {
          iceState: pc.iceConnectionState,
          iceGatheringState: pc.iceGatheringState,
          signalingState: pc.signalingState,
        });
        pc.restartIce();
        lastRestartAtByCall.set(call, Date.now());
        log('restartIce', {
          iceState: pc.iceConnectionState,
          iceGatheringState: pc.iceGatheringState,
          signalingState: pc.signalingState,
        });

        clearIceRecoveryTimers(call);
        const postT = setTimeout(() => {
          postRestartLogTimersByCall.delete(call);
          try {
            if (call.callHasEnded?.()) return;
            const conn = call.peerConn;
            if (!conn || conn !== pc) return;
            log('post-restart ICE state', {
              iceState: conn.iceConnectionState,
              iceGatheringState: conn.iceGatheringState,
              signalingState: conn.signalingState,
            });
          } catch (_) {
            /* ignore */
          }
        }, POST_RESTART_LOG_MS);
        postRestartLogTimersByCall.set(call, postT);

        try {
          const iceAfter = pc.iceConnectionState;
          if (iceAfter === 'checking') {
            log('skip renegotiation: ICE already checking');
          } else if (typeof call._negotiate === 'function') {
            if (isStaleCall(call, pc)) {
              return { ok: true };
            }
            await call._negotiate();
          } else if (typeof call.queueGotLocalOffer === 'function') {
            if (isStaleCall(call, pc)) {
              return { ok: true };
            }
            call.queueGotLocalOffer();
          }
        } catch (err) {
          log('renegotiation fallback failed', err);
        }

        return { ok: true };
      }
    } catch (err) {
      log('restartIce failed', err);
    }

    return { ok: false, reason: 'no_restart_ice' };
  } finally {
    restartInProgress.delete(call);
  }
}
